/**
 * "Signed": a request (or one of its approvals) was signed with a passkey.
 * The tooltip says who signed, in what role, and when.
 *
 * "Flagged for review", in a warning style, when a signer has since reported
 * the passkey as not theirs: the signature stands as evidence, but an admin
 * should look at the request.
 *
 * Renders nothing when there are no signatures, including on a server that
 * does not send the field yet and on a view that sends it as null.
 */
import { Box, Stack, Text, Tooltip } from "@chakra-ui/react";
import { TbAlertTriangle, TbSignature } from "react-icons/tb";

import Chip from "components/ds/Chip";

import { signatureBadge } from "./signatures";

const SignedBadge = ({ signatures }: { signatures?: unknown }) => {
  const badge = signatureBadge(signatures);
  if (!badge) return null;

  return (
    <Tooltip
      hasArrow
      placement="top"
      openDelay={150}
      bg="neutral.950"
      color="neutral.0"
      fontSize="xs"
      px="10px"
      py="6px"
      borderRadius="component.lg"
      maxW="280px"
      label={
        <Stack spacing="2xs">
          {badge.lines.map((line) => (
            <Text key={line}>{line}</Text>
          ))}
        </Stack>
      }
    >
      <Box
        as="span"
        display="inline-flex"
        tabIndex={0}
        aria-label={`${badge.flagged ? "Flagged for review" : "Signed with a passkey"}. ${badge.lines.join(". ")}.`}
        borderRadius="full"
        _focusVisible={{ boxShadow: "focusRing", outline: "none" }}
      >
        <Chip
          tone={badge.flagged ? "warning" : "success"}
          icon={
            badge.flagged ? (
              <TbAlertTriangle size={13} aria-hidden />
            ) : (
              <TbSignature size={13} aria-hidden />
            )
          }
        >
          {badge.label}
        </Chip>
      </Box>
    </Tooltip>
  );
};

export default SignedBadge;
